"use client"

import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from "react"
import { TRACKING_PARAM_KEYS, readStoredAttribution } from "../lib/attribution"

const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID?.trim() || undefined
const GOOGLE_ADS_WHATSAPP_LABEL =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_WHATSAPP_LABEL?.trim() || undefined
const GOOGLE_ADS_PHONE_LABEL = process.env.NEXT_PUBLIC_GOOGLE_ADS_PHONE_LABEL?.trim() || undefined

type LeadType = "whatsapp" | "phone"

/**
 * Reads the first touch record written on arrival by `AttributionCapture`.
 * This used to resolve the params from `window.location.search` at click time,
 * which lost them for anyone who navigated internally before converting. See
 * `src/lib/attribution.ts` for why capture moved to arrival.
 */
function withTrackingParams(href: string) {
  if (typeof window === "undefined") {
    return href
  }

  const tracking = readStoredAttribution()
  const entries = TRACKING_PARAM_KEYS.map((key) => [key, tracking[key]] as const).filter(
    ([, value]) => Boolean(value),
  )

  if (entries.length === 0) {
    return href
  }

  try {
    const url = new URL(href)
    entries.forEach(([key, value]) => {
      if (value) {
        url.searchParams.set(key, value)
      }
    })
    return url.toString()
  } catch {
    return href
  }
}

interface TrackedLeadLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children" | "onClick"> {
  children: ReactNode
  cta: string
  leadType: LeadType
}

function reportGoogleAdsConversion(sendTo: string, onComplete?: () => void) {
  if (typeof window === "undefined") {
    onComplete?.()
    return
  }

  const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag

  if (!gtag) {
    onComplete?.()
    return
  }

  let completed = false
  const callback = () => {
    if (completed) {
      return
    }
    completed = true
    onComplete?.()
  }

  gtag("event", "conversion", {
    send_to: sendTo,
    event_callback: callback,
  })

  window.setTimeout(callback, 1000)
}

function trackLeadEvent(leadType: LeadType, cta: string) {
  if (typeof window === "undefined") {
    return
  }

  const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag

  if (!gtag) {
    return
  }

  // The channel rides along on every lead event. Without it there is no way to
  // answer "how many contacts came from AI this quarter", which is the one
  // question the whole measurement exists to answer. wa.me ignores any param
  // other than `text`, so the clinic never sees these: GA4 is the record.
  const attribution = readStoredAttribution()

  const eventName = leadType === "whatsapp" ? "whatsapp_click" : "phone_call_click"
  const baseEventData = {
    event_category: "lead",
    event_label: cta,
    value: 1,
    cta_location: cta,
    canal: attribution.channel ?? "desconhecido",
    referrer_host: attribution.referrer_host ?? "",
    landing_path: attribution.landing_path ?? "",
  }

  if (leadType === "whatsapp") {
    gtag("event", eventName, {
      ...baseEventData,
      page: window.location.pathname,
      source: "cta_button",
    })

    return
  }

  gtag("event", "phone_click", {
    page: window.location.pathname,
  })

  gtag("event", eventName, baseEventData)
}

export function TrackedLeadLink({
  href = "#",
  target,
  rel,
  children,
  cta,
  leadType,
  ...rest
}: TrackedLeadLinkProps) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()

    trackLeadEvent(leadType, cta)

    const navigate = () => {
      if (leadType === "whatsapp") {
        window.open(withTrackingParams(href), "_blank", "noopener,noreferrer")
        return
      }

      if (target === "_blank") {
        window.open(href, "_blank", "noopener,noreferrer")
        return
      }

      window.location.href = href
    }

    const label =
      leadType === "whatsapp" ? GOOGLE_ADS_WHATSAPP_LABEL : GOOGLE_ADS_PHONE_LABEL

    if (GOOGLE_ADS_ID && label) {
      reportGoogleAdsConversion(`${GOOGLE_ADS_ID}/${label}`, navigate)
      return
    }

    navigate()
  }

  return (
    <a href={href} target={target} rel={rel} onClick={handleClick} {...rest}>
      {children}
    </a>
  )
}
